const API_BASE = "/api";

// CSRF token received from server (set on login and /auth/me)
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export class ApiError extends Error {
  status: number;
  errorCode?: string;
  headers?: string[];
  constructor(message: string, status: number, errorCode?: string, headers?: string[]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.headers = headers;
  }
}

async function parseErrorResponse(res: Response): Promise<{ message?: string; error?: string; code?: string; headers?: string[] }> {
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return res.json().catch(() => ({ message: res.statusText || `HTTP ${res.status}` }));
  }
  const text = await res.text().catch(() => "");
  const plainText = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { message: (plainText || res.statusText || `HTTP ${res.status}`).slice(0, 300) };
}

function isCsrfMismatch(res: Response, error: { message?: string; error?: string }): boolean {
  return res.status === 403 && (error.error === "CSRF token mismatch" || error.message === "CSRF token mismatch");
}

async function refreshCsrfToken(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
  if (!res.ok) return false;
  const data = await res.json().catch(() => null) as { csrfToken?: string } | null;
  if (!data?.csrfToken) return false;
  setCsrfToken(data.csrfToken);
  return true;
}

async function request<T>(path: string, options?: RequestInit, retriedCsrf = false): Promise<T> {
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {} // Let browser set Content-Type with boundary
    : { "Content-Type": "application/json" };
  // Attach CSRF token for state-changing requests
  const method = options?.method?.toUpperCase() ?? "GET";
  if (csrfToken && method !== "GET" && method !== "HEAD") {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirect = current && current !== "/login" ? `?redirect=${encodeURIComponent(current)}` : "";
    window.location.href = `/login${redirect}`;
    throw new ApiError("Unauthorized", 401);
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res);
    if (!retriedCsrf && isCsrfMismatch(res, error) && await refreshCsrfToken()) {
      return request<T>(path, options, true);
    }
    throw new ApiError(
      error.message || error.error || `HTTP ${res.status}`,
      res.status,
      error.code ?? error.error,
      error.headers,
    );
  }
  return res.json();
}

async function requestBlob(path: string, options?: RequestInit, retriedCsrf = false): Promise<Blob> {
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {}
    : { "Content-Type": "application/json" };
  const method = options?.method?.toUpperCase() ?? "GET";
  if (csrfToken && method !== "GET" && method !== "HEAD") {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirect = current && current !== "/login" ? `?redirect=${encodeURIComponent(current)}` : "";
    window.location.href = `/login${redirect}`;
    throw new ApiError("Unauthorized", 401);
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res);
    if (!retriedCsrf && isCsrfMismatch(res, error) && await refreshCsrfToken()) {
      return requestBlob(path, options, true);
    }
    throw new ApiError(
      error.message || error.error || `HTTP ${res.status}`,
      res.status,
      error.code ?? error.error,
      error.headers,
    );
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, {
      method: "POST",
      body: formData,
      headers: {},  // Let browser set Content-Type with boundary
    }),
  postBlob: (path: string, body: unknown) =>
    requestBlob(path, { method: "POST", body: JSON.stringify(body) }),
};
